import { CampaignWithNodeDetails } from './types';
import { _campaignCache } from './cache';
import { nodeService } from './nodeService';
import { supabase } from '../../lib/supabase';

// In-flight promise map to dedupe concurrent requests
const _inFlight = new Map<string, Promise<any>>();

async function getCampaigns(
    userId: string,
    page: number = 1,
    limit: number = 50
  ): Promise<{ success: boolean; data?: CampaignWithNodeDetails[]; total?: number; page?: number; limit?: number; error?: string }> {
    try {
      const now = Date.now();
      const cacheKey = `${userId}:${page}:${limit}`;

      const cached = _campaignCache.get(cacheKey);
      if (cached && (now - cached.ts) < cached.ttl) {
        return { success: true, data: cached.data, total: cached.data?.length, page, limit };
      }

      const from = (page - 1) * limit;
      const to = from + limit - 1;

      // Fetch campaigns with campaign_nodes join - use campaign_nodes as source of truth
      // selected_nodes is deprecated and no longer used
      const { data: campaigns, error, count } = await supabase
        .from('campaigns')
        .select(`
          *,
          campaign_nodes (
            node_id,
            nodes (id, name, location, status, price, device_type)
          )
        `, { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('❌ Error fetching campaigns:', error);
        return { success: false, error: error.message };
      }

      const campaignsArray = campaigns || [];

      // Process campaigns to extract node relationships from campaign_nodes bridge table
      const campaignsWithNodeDetails = campaignsArray.map((campaign: any) => {
        const campaignNodeData = campaign.campaign_nodes || [];
        const nodeDetails = campaignNodeData
          .map((cn: any) => cn.nodes)
          .filter(Boolean);

        return {
          ...campaign,
          campaign_nodes: campaignNodeData,
          selected_nodes: campaignNodeData.map((cn: any) => cn.node_id), // Derived from campaign_nodes
          selectedNodeDetails: nodeDetails,
          nodes: nodeDetails,
          nodeDetails: nodeDetails
        };
      });

      // cache campaigns per user for 5 minutes (increased from 30s)
      _campaignCache.set(cacheKey, { data: campaignsWithNodeDetails, ts: now, ttl: 1000 * 60 * 5 });

      return { success: true, data: campaignsWithNodeDetails, total: count || 0, page, limit };
    } catch (error) {
      console.error('💥 Unexpected error in getCampaigns:', error);
      return { success: false, error: 'Unexpected error occurred' };
    }
}

// Get campaigns with their associated campaign_nodes and direct node details
async function getCampaignsWithAdSlots(
    userId: string,
    page: number = 1,
    limit: number = 50
  ): Promise<{ success: boolean; data?: any[]; total?: number; page?: number; limit?: number; error?: string }> {
    const key = `getCampaignsWithAdSlots:${userId}:${page}:${limit}`;
    const now = Date.now();

    // Check for cached data first
    const cached = _campaignCache.get(key);
    if (cached && (now - cached.ts) < cached.ttl) {
      return { success: true, data: cached.data, total: cached.data?.length };
    }

    // Check in-flight promises to avoid duplicate requests
    if (campaignReadService._inFlight.has(key)) {
      return campaignReadService._inFlight.get(key) as Promise<any>;
    }

    const promise = (async () => {
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        
        // Get campaigns with node relationships through campaign_nodes bridge table
        const { data: campaigns, error: campaignsError, count } = await supabase
          .from('campaigns')
          .select(`
            *,
            campaign_nodes (
              node_id,
              nodes (
                id,
                name,
                location,
                device_type,
                price,
                status,
                description
              )
            )
          `, { count: 'exact' })
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (campaignsError) {
          console.error('❌ Error fetching campaigns with bridge table:', campaignsError);
          return { success: false, error: campaignsError.message };
        }

        if (!campaigns || campaigns.length === 0) {
          return { success: true, data: [], total: 0, page, limit };
        }

        // Process campaigns to extract node relationships from bridge table data
        const campaignsWithSlots = campaigns.map(campaign => {
          // Extract campaign_nodes data from the nested query result
          const campaignNodeData = campaign.campaign_nodes || [];
          const selected_nodes = campaignNodeData.map((cn: any) => cn.node_id);

          // Extract node details from the nested nodes data
          const nodeDetails = campaignNodeData.map((cn: any) => cn.nodes).filter(Boolean);

          return {
            ...campaign,
            campaign_nodes: campaignNodeData,
            selected_nodes: selected_nodes,
            selectedNodeDetails: nodeDetails,
            nodes: nodeDetails, // For backward compatibility
            direct_node: nodeDetails.length > 0 ? nodeDetails[0] : null // First node as direct_node
          };
        });

        // Debug logging removed

        // Cache the result (increased from 30s to 5 minutes)
        _campaignCache.set(key, { data: campaignsWithSlots, ts: now, ttl: 1000 * 60 * 5 });

        return { success: true, data: campaignsWithSlots, total: count || 0, page, limit };
      } catch (error) {
        console.error('💥 Error in getCampaignsWithAdSlots:', error);
        return { success: false, error: 'Unexpected error occurred' };
      } finally {
        campaignReadService._inFlight.delete(key);
      }
    })();

    campaignReadService._inFlight.set(key, promise);
    return promise;
}

// Get a single campaign by ID with all details (optimized for preview modals)
async function getCampaignById(campaignId: string, userId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    const key = `getCampaignById:${campaignId}`;
    const now = Date.now();

    // Check in-flight promises to avoid duplicate requests
    if (campaignReadService._inFlight.has(key)) {
      return campaignReadService._inFlight.get(key) as Promise<any>;
    }

    const promise = (async () => {
      try {

        // Fetch single campaign with campaign_nodes, node details, and QR code data in ONE query
        const { data: campaign, error: campaignError } = await supabase
          .from('campaigns')
          .select(`
            *,
            campaign_nodes (
              id,
              node_id,
              campaign_id,
              created_at,
              nodes:node_id (
                id,
                name,
                location,
                price,
                device_type,
                status,
                description
              )
            ),
            qr_codes!fk_qr_codes_campaign (
              id,
              qr_code_url,
              destination_url,
              cta,
              cta_icon,
              qr_type,
              status,
              design_settings
            )
          `)
          .eq('id', campaignId)
          .eq('user_id', userId)
          .single();

        if (campaignError) {
          console.error('❌ Error fetching campaign:', campaignError);
          return { success: false, error: campaignError.message };
        }

        if (!campaign) {
          return { success: false, error: 'Campaign not found' };
        }

        // Extract campaign_nodes data and reconstruct fields for backward compatibility
        const campaignNodeData = campaign.campaign_nodes || [];
        const selected_nodes = campaignNodeData.map((cn: any) => cn.node_id);
        const nodeDetails = campaignNodeData.map((cn: any) => cn.nodes).filter(Boolean);

        // Extract QR code data
        const qrCodeData = campaign.qr_codes && Array.isArray(campaign.qr_codes) && campaign.qr_codes.length > 0
          ? campaign.qr_codes[0]
          : campaign.qr_codes;

        const enrichedCampaign = {
          ...campaign,
          selected_nodes,
          direct_node: nodeDetails.length > 0 ? nodeDetails[0] : null, // First node as direct_node
          selectedNodeDetails: nodeDetails,
          nodes: nodeDetails, // For backward compatibility
          campaign_nodes: campaignNodeData,
          // Add QR code data in format expected by AdPreview
          qr_code: qrCodeData,
          qr_code_url: qrCodeData?.qr_code_url,
          destination_url: qrCodeData?.destination_url,
          qr_image_url: qrCodeData?.qr_code_url, // AdPreview looks for qr_image_url
          qr_cta: qrCodeData?.cta,
          qr_type: qrCodeData?.qr_type || campaign.qr_type,
          qr_status: qrCodeData?.status,
          qr_design_settings: qrCodeData?.design_settings,
          qr_id: qrCodeData?.id, // For QR tracking in buildFinalUrlFromCampaign
          qr_url: qrCodeData?.qr_code_url // Fallback URL field
        };

        // Debug logging removed

        return { success: true, data: enrichedCampaign };
      } catch (error) {
        console.error('💥 Error in getCampaignById:', error);
        return { success: false, error: 'Unexpected error occurred' };
      } finally {
        campaignReadService._inFlight.delete(key);
      }
    })();

    campaignReadService._inFlight.set(key, promise);
    return promise;
}

export const campaignReadService = {
    getCampaigns,
    getCampaignsWithAdSlots,
    getCampaignById,
    _inFlight,
};
